/**
 * The persisted-scenario vocabulary, shared by the pipeline screen and by the
 * banner the sizers wear when they are pricing one.
 *
 * Both surfaces read the SAME doc (`pricing_scenarios`: metadata + an
 * append-only `versions[]`, each version carrying the inputs it priced and the
 * result it got), and both have to say the same things about it — which sizer
 * owns it, what its headline figure is, how far it has drifted from v1, and
 * where "open this version" goes. Keeping that in one module is what stops the
 * banner and the pipeline disagreeing about a scenario they are both looking at.
 */
import { isScenarioId, readScenarioId } from "../../api/sizerApi";
import {
  fmtMoney,
  fmtPct,
  fmtPrice,
  fmtRate,
  fmtRatio,
  humanize,
} from "./sizerFormat";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";
import { rtlRequiredCoupon } from "./rtlRateDisplay";
import {
  CASH_OUT_REFI,
  LEGACY_DELAYED_PURCHASE,
  isDelayedPurchase,
} from "../../utils/loans/delayedPurchase";

/* Re-exported so a screen reaching for the scenario vocabulary gets the id
   guard with it: an id that did not resolve must never reach a URL, in a fetch
   path or a link (see sizerApi for what a "null" in a path actually does). */
export { isScenarioId, readScenarioId };

/* ============================================================ Identity */

// `scenarioType` decides which sizer owns a scenario — where "Open in sizer"
// goes, and which figures its versions are summarized by. DSCR Quick Entry
// writes `dscr_quick` too, and resumes into the full DSCR sizer: that form is
// the superset, so nothing stored can fail to load.
export const TYPE_META = {
  rtl: { label: "RTL", tab: "rtl" },
  guc: { label: "GUC", tab: "guc" },
  dscr_quick: { label: "DSCR", tab: "dscr" },
};

/**
 * Which sizer a scenario BELONGS to. The RTL and GUC sizers both save
 * `scenarioType: "rtl"` (one backend enum, one endpoint); since GUC has its own
 * sizer the product tells them apart — a Ground Up Construction scenario is a
 * GUC scenario and re-opens in the GUC Sizer. `guc` is a display / routing key
 * only, never posted.
 */
export const scenarioKind = (row) => {
  const t = row?.scenarioType;
  const product =
    row?.scenario?.loanProductType ?? row?.inputs?.loanProductType;
  if (t === "rtl" && product === "Ground Up Construction") return "guc";
  return t;
};

export const typeMeta = (t) =>
  TYPE_META[t] || { label: humanize(t || "—"), tab: null };

export const STATUS_TONE = {
  Active: "live",
  Converted: "est",
  Archived: "idle",
};

export const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Active", label: "Active" },
  { value: "Converted", label: "Converted" },
  { value: "Archived", label: "Archived" },
];

export const idOf = (row) => row?.scenarioId || row?._id || row?.id;

export const errText = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

/**
 * Where a scenario re-opens. `version` deep-links a SPECIFIC version's inputs —
 * still a resume of the same scenario, not a rollback: re-pricing from there
 * appends the next version like any other edit.
 *
 * `mode` picks the scenario workspace view: "review" (the read-only results
 * page a list row opens) or "edit" (the form + re-price rail).
 */
export function sizerPathFor(scenarioType, scenarioId, version, { mode } = {}) {
  const { tab } = typeMeta(scenarioType);
  if (!tab || !isScenarioId(scenarioId)) return null;
  return (
    `/sizers/${tab}?scenario=${scenarioId}` +
    (version ? `&version=${version}` : "") +
    (mode ? `&mode=${mode}` : "")
  );
}

/**
 * The short "what changed between versions" line the version picker shows
 * under each version: the inputs that most often move between reprices.
 */
export function versionInputLine(scenarioType, scenario) {
  const s = scenario || {};
  if (scenarioType === "dscr_quick") {
    return [
      fmtMoney(s.requestedLoanAmount, { dash: null }),
      s.loanPurpose,
      s.resolvedCreditScore || s.ficoMid
        ? `FICO ${s.resolvedCreditScore || s.ficoMid}`
        : null,
      s.requestedCoupon ? fmtRate(s.requestedCoupon) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return [
    s.termMonths ? `${s.termMonths} mo` : null,
    s.sizerInitialRequestedLeverage != null
      ? fmtPct(s.sizerInitialRequestedLeverage, 0)
      : fmtMoney(s.baseLoanAmount, { dash: null }),
    s.loanPurpose,
  ]
    .filter(Boolean)
    .join(" · ");
}

/* ============================================================ Figures */

/**
 * The headline figures a version is compared BY — one row of the history table,
 * one line of the banner's version list.
 *
 * Read off the stored `result` and nothing else: these are the numbers the
 * engine returned for that version, so a history row shows what actually priced
 * at the time, not what today's engine would say about yesterday's inputs.
 */
export function versionFigures(scenarioType, result) {
  if (!result) return {};
  if (scenarioType === "dscr_quick") {
    const best = result.result || {};
    return {
      primary: fmtRate(best.coupon),
      primaryLabel: "Coupon",
      secondary: fmtPrice(best.price),
      secondaryLabel: "Price",
      tertiary: fmtRatio(best.dscr, 3),
      tertiaryLabel: "DSCR",
      primaryRaw: best.coupon,
    };
  }
  const ltv = (result.leverage || []).find((m) => m.metric === "LTV");
  return {
    primary: fmtRate(rtlRequiredCoupon(result.rate)),
    primaryLabel: "Required coupon",
    secondary: fmtPct(ltv?.requested, 2),
    secondaryLabel: "Requested LTV",
    tertiary: result.resolved?.borrowerClass || "—",
    tertiaryLabel: "Class",
    primaryRaw: rtlRequiredCoupon(result.rate),
  };
}

/**
 * The version history, newest first, each row carrying its figures and its
 * drift from v1 in basis points.
 *
 * Drift is a difference between two RECORDED numbers of the same engine — v1 is
 * the terms that were first declared, and every later version is measured off
 * it. Nothing here re-prices anything.
 */
export function versionRows(doc) {
  const type = doc?.scenarioType;
  const sorted = [...(doc?.versions || [])].sort(
    (a, b) => (b.version || 0) - (a.version || 0),
  );
  const first = sorted.length ? sorted[sorted.length - 1] : null;
  const base = versionFigures(type, first?.result);
  const rows = sorted.map((v) => {
    const f = versionFigures(type, v.result);
    const driftBps =
      f.primaryRaw != null && base.primaryRaw != null
        ? Math.round((f.primaryRaw - base.primaryRaw) * 10000)
        : null;
    return { ...v, ...f, driftBps };
  });
  return { rows, base };
}

/** `+75 bps` / `0 bps` / `—` — the drift label, signed. */
export const fmtDrift = (bps) =>
  bps == null ? "—" : `${bps > 0 ? "+" : ""}${bps} bps`;

/* ============================================================ Inputs */

const first = (arr) => (Array.isArray(arr) ? arr[0] : null) || {};

const addressLine = (a) =>
  addressLabel(a) ||
  [a?.streetAddress || a?.street, a?.city, a?.state, a?.zip]
    .filter(Boolean)
    .join(", ") ||
  null;

const cell = (k, v, note) => (v == null || v === "" ? null : { k, v, note });

/**
 * The one-line description a scenario row wears under its name in the list:
 * where it is and what kind of loan it is. Read off the CURRENT inputs (the list
 * route carries `scenario` but not `result`), so it says what was asked for,
 * never what priced.
 */
export function scenarioSubtitle(scenarioType, scenario) {
  const s = scenario || {};
  if (scenarioType === "dscr_quick") {
    return [
      addressLine(s.address) || s.zipCode,
      s.loanPurpose,
      s.propertyType || (s.condo ? "Condo" : null),
      s.unitCount ? `${s.unitCount} unit${s.unitCount === 1 ? "" : "s"}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  const p = first(s.subjectProperties);
  return [
    addressLine(p?.address) || p?.address?.zip,
    s.loanProductType,
    s.loanPurpose,
    s.termMonths ? `${s.termMonths} mo` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The loan amount the scenario ASKED for, from its inputs. DSCR always states
 * one; RTL / GUC usually size by leverage instead, so this is null unless the
 * amount was entered as an override — `scenarioRequestedLeverage` is that case.
 */
export const scenarioRequestedAmount = (scenarioType, scenario) => {
  const s = scenario || {};
  const v =
    scenarioType === "dscr_quick"
      ? s.requestedLoanAmount
      : (s.totalLoanAmount ?? s.baseLoanAmount);
  return v == null || v === "" ? null : Number(v);
};

/** RTL / GUC requested leverage (a fraction), or null. DSCR sizes by amount. */
export const scenarioRequestedLeverage = (scenarioType, scenario) => {
  if (scenarioType === "dscr_quick") return null;
  const v = scenario?.sizerInitialRequestedLeverage;
  return v == null || v === "" ? null : Number(v);
};

/**
 * The stored payload, read as a person reads it.
 *
 * The scenario speaks loan-model vocabulary by design, which makes it perfect to
 * post and terrible to skim — `resolvedCreditScore: 740` next to a nested
 * `address` object is a contract, not a summary. This picks the handful of
 * fields that DECIDE the price for each sizer and labels them; the raw payload
 * stays one click away for anyone checking the contract itself.
 */
export function scenarioInputSummary(scenarioType, scenario) {
  const s = scenario || {};
  if (!Object.keys(s).length) return [];

  if (scenarioType === "dscr_quick") {
    return [
      cell(
        "Purpose",
        s.loanPurpose === LEGACY_DELAYED_PURCHASE
          ? CASH_OUT_REFI
          : s.loanPurpose,
        isDelayedPurchase(s.loanPurpose, s.delayedPurchase)
          ? "delayed purchase"
          : null,
      ),
      cell("Loan amount", fmtMoney(s.requestedLoanAmount, { dash: null })),
      cell(
        "Requested coupon",
        s.requestedCoupon ? fmtRate(s.requestedCoupon) : "Best execution",
      ),
      cell("FICO", s.resolvedCreditScore || s.ficoMid),
      cell(
        "As-is value",
        fmtMoney(s.asIsValue, { dash: null }),
        s.purchasePrice && s.purchasePrice === s.asIsValue ? "= price" : null,
      ),
      cell("Purchase price", fmtMoney(s.purchasePrice, { dash: null })),
      cell("Property", s.propertyType || (s.condo ? "Condo" : null)),
      cell("Address", addressLine(s.address) || s.zipCode),
      cell(
        "Rents",
        fmtMoney(s.totalRents ?? s.totalMarketRents, { dash: null }),
        s.vacancySelection || null,
      ),
      cell(
        "Taxes / Ins.",
        s.annualTaxes || s.annualInsurance
          ? `${fmtMoney(s.annualTaxes, { dash: "—" })} / ${fmtMoney(s.annualInsurance, { dash: "—" })}`
          : null,
      ),
      cell("Units", s.unitCount),
      cell("Prepay", s.prepaymentPenaltyStructure),
    ].filter(Boolean);
  }

  // RTL / GUC — the scenario is a small loan: terms, the borrowing entity, the
  // guarantor that carries the class, and the one subject property.
  const b = first(s.borrowers);
  const p = first(s.subjectProperties);
  const e =
    (s.entities || []).find((x) => x?.borrowerEntity) || first(s.entities);
  return [
    cell("Product", s.loanProductType),
    cell(
      "Purpose",
      s.loanPurpose === LEGACY_DELAYED_PURCHASE ? CASH_OUT_REFI : s.loanPurpose,
      isDelayedPurchase(s.loanPurpose, s.delayedPurchase)
        ? "delayed purchase"
        : null,
    ),
    cell("Term", s.termMonths ? `${s.termMonths} mo` : null),
    cell(
      "Requested leverage",
      s.sizerInitialRequestedLeverage != null
        ? fmtPct(s.sizerInitialRequestedLeverage, 1)
        : null,
    ),
    // Sized by amount instead of leverage (either is allowed).
    cell(
      "Initial loan amount",
      s.sizerInitialRequestedLeverage == null
        ? fmtMoney(s.baseLoanAmount, { dash: null })
        : null,
    ),
    cell("Entity", e?.entityName, e?.entityType || null),
    cell("Guarantor FICO", b?.ficoMid, b?.citizenshipStatus || null),
    cell(
      "Experience",
      b?.experience
        ? Object.entries(b.experience)
            .map(([k, v]) => `${v} ${humanize(k).toLowerCase()}`)
            .join(" · ")
        : null,
    ),
    cell("Address", addressLine(p?.address) || p?.address?.zip),
    cell("Property", p?.propertyType),
    cell("As-is value", fmtMoney(p?.asIsValue, { dash: null })),
    cell("Purchase price", fmtMoney(p?.purchasePrice, { dash: null })),
    cell("Rehab budget", fmtMoney(p?.constructionBudget, { dash: null })),
    cell("ARV", fmtMoney(p?.arv, { dash: null })),
  ].filter(Boolean);
}
